import { useEffect } from 'react';

import { SimpleSelect } from '@/components/ui/simple-select';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Label } from '@/components/ui/label';
import {
  photosRadioOptions,
  tourRadioOptions,
  activeStatusRadioOptions,
  TRISTATE_EITHER,
  TRISTATE_NO,
  TRISTATE_YES,
  ACTIVE_STATUS_ACTIVE
} from '../more-filters/metadata/FilterOption';
import { LastOrderDateOptions } from '../../../../config/options.config';
import { parseQueryString } from '../utils/advancedFilterUtils';

export interface PropertyAdvancedFiltersProps {
  filter: string;
  onPhotosChange: (value: string) => void;
  onTourChange: (value: string) => void;
  onLastOrderDateChange: (value: string) => void;
  onOccupancyStatusChange: (value: string) => void;
  onLeaseStatusChange: (value: string) => void;
  onPortfolioChange: (value: string) => void;
  onActiveStatusChange: (value: string) => void;
  photosFilter: string;
  tourFilter: string;
  lastOrderDate: string;
  activeStatusFilter: string;
}

// Hydrates persisted moreFilters query string (?hasPhotos=true&has3DTour=false)
// back into the tri-state value space.
const tristateFromBool: Record<string, string> = {
  true: TRISTATE_YES,
  false: TRISTATE_NO
};

// Radix Select forbids an empty-string item value, so "Any time" (= no
// last-order-date filter) uses a non-empty sentinel that maps back to ''.
const ANY_TIME_VALUE = 'any-time';
const lastOrderDateOptions = [
  { value: ANY_TIME_VALUE, label: 'Any time' },
  ...LastOrderDateOptions
];

const SectionHeader = ({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string;
}) => (
  <div className="mb-2">
    <h3 className="text-base font-semibold uppercase tracking-wide text-muted-foreground">
      {title}
    </h3>
    {subtitle && (
      <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
    )}
  </div>
);

const FilterRow = ({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
    <Label className="mb-0">{label}</Label>
    {children}
  </div>
);

const PropertyAdvancedFilters = ({
  filter,
  onPhotosChange,
  onTourChange,
  onLastOrderDateChange,
  onOccupancyStatusChange,
  onLeaseStatusChange,
  onPortfolioChange,
  onActiveStatusChange,
  photosFilter,
  tourFilter,
  lastOrderDate,
  activeStatusFilter
}: PropertyAdvancedFiltersProps) => {
  // Mirror the parent's moreFilters string into the drawer's local state.
  // Always reset to defaults first, then overlay whatever the QS carries —
  // so an empty QS (Clear Filters / Reset) clears the controls, and a partial
  // QS doesn't leave previously-set fields stranded at their old value.
  useEffect(() => {
    const values = filter ? parseQueryString(filter) : {};
    onPhotosChange(tristateFromBool[values.hasPhotos] ?? TRISTATE_EITHER);
    onTourChange(tristateFromBool[values.has3DTour] ?? TRISTATE_EITHER);
    onLastOrderDateChange(values.lastOrderDateRange ?? '');
    onOccupancyStatusChange(values.propertyStatus ?? '');
    onLeaseStatusChange(values.leaseStatus ?? '');
    onPortfolioChange(values.portfolioID ?? '');
  }, [filter]);

  const handleLastOrderDateChange = (_field: string, value: string) => {
    onLastOrderDateChange(value === ANY_TIME_VALUE ? '' : value);
  };

  return (
    <div className="space-y-8">
      <section>
        <SectionHeader
          title="Assets"
          subtitle="What we've captured for the property."
        />
        <FilterRow label="Photos">
          <SegmentedControl
            name="photosFilter"
            aria-label="Photos"
            options={photosRadioOptions}
            value={photosFilter || TRISTATE_EITHER}
            onValueChange={onPhotosChange}
          />
        </FilterRow>
        <FilterRow label="3D Tour">
          <SegmentedControl
            name="tourFilter"
            aria-label="3D Tour"
            options={tourRadioOptions}
            value={tourFilter || TRISTATE_EITHER}
            onValueChange={onTourChange}
          />
        </FilterRow>
      </section>

      <hr className="border-border" />

      <section>
        <SectionHeader title="Property" />
        <div className="py-3">
          <Label htmlFor="lastOrderDate" className="mb-3">
            Last Order Date
          </Label>
          <SimpleSelect
            options={lastOrderDateOptions}
            name="lastOrderDate"
            md="12"
            className="[&_button]:cursor-pointer"
            controlId="lastOrderDate"
            ariaDescribedBy="lastOrderDate-select"
            placeholder="Any time"
            value={
              lastOrderDate
                ? { value: lastOrderDate, label: lastOrderDate }
                : { value: ANY_TIME_VALUE, label: 'Any time' }
            }
            setFieldValue={handleLastOrderDateChange}
            field="lastOrderDate"
          />
        </div>
        <FilterRow label="Status">
          <SegmentedControl
            name="activeStatusFilter"
            aria-label="Status"
            options={activeStatusRadioOptions}
            value={activeStatusFilter || ACTIVE_STATUS_ACTIVE}
            onValueChange={onActiveStatusChange}
          />
        </FilterRow>
      </section>
    </div>
  );
};

export default PropertyAdvancedFilters;
